import { Radio, Space } from 'antd';
import { FormCheckbox, FormInput, FormRadio, FormWrapper, UploadInput } from 'components/form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
// import { CardTitle } from 'components/claimCard/CardTitle';
import { CurrencySelect } from './CurrencySelect';
import { useCallback, useEffect, useRef } from 'react';
import { breakpoint } from 'styled-components-breakpoint';
import './claim.less';

interface ClaimAmountCardProps {
  formStepIndex?: number;
  includeSettlementFile?: boolean;
}

const Wrapper = styled.div`
  display: grid;
  row-gap: 24px;
  ${breakpoint('desktop')`
    width: 70%;
  `}
`;

interface PartialClaimItemProps {
  partialClaim?: boolean;
  isFirstClaim?: boolean;
  firstClaimWithGenerali?: boolean;
  includeSettlementFile?: boolean;
}
const IsFirstClaimRadio = ({ partialClaim }: PartialClaimItemProps) => {
  const { t } = useTranslation();
  if (!partialClaim) return <></>;
  return (
    <FormWrapper name="isFirstClaim" label={t('newClaim.start.partialClaim.partialClaimTypeTitle')}>
      <FormRadio name="isFirstClaim">
        <Space className="isFirstClaim">
          <Radio value={true} style={{ flex: 1 }}>
            {t('newClaim.start.partialClaim.partialClaimTypeRadio1')}
          </Radio>
          <Radio value={false} style={{ flex: 1 }}>
            {t('newClaim.start.partialClaim.partialClaimTypeRadio2')}
          </Radio>
        </Space>
      </FormRadio>
    </FormWrapper>
  );
};

const OtherGeneraliClaim = () => {
  const { t } = useTranslation();

  return (
    <>
      <FormWrapper
        name="anotherPolicyNumber"
        label={t('newClaim.partialClaim.firstClaim.anotherGeneraliPolicyNoTitle')}
      >
        <FormInput
          name="anotherPolicyNumber"
          placeholder={t('newClaim.partialClaim.firstClaim.anotherGeneraliPolicyNoPlaceholder')}
        />
      </FormWrapper>
      <FormWrapper name="anotherMemberId" label={t('newClaim.partialClaim.firstClaim.anotherGeneraliMemberIdTitle')}>
        <FormInput
          name="anotherMemberId"
          placeholder={t('newClaim.partialClaim.firstClaim.anotherGeneraliMemberIdPlaceholder', { digit: '12' })}
        />
      </FormWrapper>
    </>
  );
};

const FirstClaimInput = ({ partialClaim, isFirstClaim }: PartialClaimItemProps) => {
  const { t } = useTranslation();

  if (partialClaim && isFirstClaim) return <OtherGeneraliClaim />;
  if (partialClaim && isFirstClaim === false)
    return (
      <FormWrapper name="firstClaimWithGenerali" label={t('newClaim.start.partialClaim.firstClaimTypeTitle')}>
        <FormRadio name="firstClaimWithGenerali">
          <Space>
            <Radio value={true}>{t('newClaim.start.partialClaim.firstClaimTypeRadio1')}</Radio>
            <Radio value={false}>{t('newClaim.start.partialClaim.firstClaimTypeRadio2')}</Radio>
          </Space>
        </FormRadio>
      </FormWrapper>
    );

  return <></>;
};

const FirstClaimWithGeneraliInput = ({
  partialClaim,
  isFirstClaim,
  firstClaimWithGenerali,
  includeSettlementFile,
}: PartialClaimItemProps) => {
  const { t } = useTranslation();

  if (partialClaim && isFirstClaim === false && firstClaimWithGenerali) return <OtherGeneraliClaim />;
  if (partialClaim && isFirstClaim === false && firstClaimWithGenerali === false)
    return (
      <>
        <FormWrapper
          name="otherInsurerName"
          label={t('newClaim.partialClaim.secondClaim.insurerNameTitle')}
          instructionText={t('newClaim.partialClaim.secondClaim.description')}
        >
          <FormInput
            name="otherInsurerName"
            placeholder={t('newClaim.partialClaim.secondClaim.insurerNamePlaceholder')}
          />
        </FormWrapper>
        <CurrencySelect name="firstClaimCurrency" label={t('newClaim.partialClaim.secondClaim.claimCurrencyTitle')} />
        <FormWrapper name="firstClaimAmount" label={t('newClaim.partialClaim.secondClaim.claimAmountTitle')}>
          <FormInput
            name="firstClaimAmount"
            placeholder={t('newClaim.partialClaim.secondClaim.claimAmountPlaceholder')}
            isCurrencyInput
            addonAfter="$"
          />
        </FormWrapper>
        {includeSettlementFile && (
          <FormWrapper name="prevClaimSettlements" label={t('newClaim.partialClaim.secondClaim.settlementAdviceTitle')}>
            <UploadInput name="prevClaimSettlements" />
          </FormWrapper>
        )}
      </>
    );
  return <></>;
};

export const PartialClaimCard = ({
  //  formStepIndex = 2,
  includeSettlementFile = true,
}: ClaimAmountCardProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const didMountRef = useRef(false);
  const partialClaim = watch('partialClaim');
  const isFirstClaim = watch('isFirstClaim');
  const firstClaimWithGenerali = watch('firstClaimWithGenerali');
  const firstClaimCurrency = watch('firstClaimCurrency');

  const clearOtherGeneraliInputValue = useCallback(() => {
    setValue('anotherPolicyNumber', '');
    setValue('anotherMemberId', '');
  }, [setValue]);
  const clearOtherIssuerInputValue = useCallback(() => {
    setValue('otherInsurerName', '');
    setValue('firstClaimCurrency', '');
    setValue('firstClaimAmount', '');
  }, [setValue]);

  useEffect(() => {
    if (partialClaim === false) {
      setValue('isFirstClaim', undefined);
      setValue('firstClaimWithGenerali', undefined);
      clearOtherGeneraliInputValue();
      clearOtherIssuerInputValue();
    } else {
      if (!firstClaimCurrency) setValue('firstClaimCurrency', 'HKD');
    }
  }, [partialClaim, setValue, clearOtherIssuerInputValue, clearOtherGeneraliInputValue, firstClaimCurrency]);

  useEffect(() => {
    if (!didMountRef.current) {
      return;
    }
    if (isFirstClaim === false) {
      setValue('firstClaimWithGenerali', true);
    }
  }, [isFirstClaim, setValue]);

  useEffect(() => {
    if (!didMountRef.current) {
      return;
    }
    if (firstClaimWithGenerali === true) {
      clearOtherIssuerInputValue();
    }
  }, [clearOtherIssuerInputValue, firstClaimWithGenerali]);

  useEffect(() => {
    if (!didMountRef.current) {
      return;
    }
    if (firstClaimWithGenerali === false) {
      clearOtherGeneraliInputValue();
    }
  }, [clearOtherGeneraliInputValue, firstClaimWithGenerali]);

  useEffect(() => {
    if (partialClaim === undefined || isFirstClaim === undefined || firstClaimWithGenerali === undefined) {
      return;
    }
    didMountRef.current = true;
  }, [partialClaim, isFirstClaim, firstClaimWithGenerali]);

  return (
    // <Card
    //   id="partialClaim"
    //   title={<CardTitle formStepIndex={formStepIndex} title={t('newClaim.partialClaim.title')} />}
    // >
    <Wrapper>
      <FormWrapper
        name="partialClaim"
        label={t('newClaim.start.partialClaim.selectionTitle')}
        // optionalText={t('common.optional')}
        // secondaryText={t('newClaim.start.partialClaim.selectionDescription')}
      >
        <FormCheckbox name="partialClaim">{t('newClaim.start.partialClaim.selectionCheckbox')}</FormCheckbox>
      </FormWrapper>
      <IsFirstClaimRadio partialClaim={partialClaim} />
      <FirstClaimInput partialClaim={partialClaim} isFirstClaim={isFirstClaim} />
      <FirstClaimWithGeneraliInput
        partialClaim={partialClaim}
        isFirstClaim={isFirstClaim}
        firstClaimWithGenerali={firstClaimWithGenerali}
        includeSettlementFile={includeSettlementFile}
      />
    </Wrapper>
    // </Card>
  );
};
